import { useState } from 'react';
import { ListPlus, ExternalLink, Pencil, Save, Star, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '@/components/ui/Modal';
import { useMutation, useQuery, useQueryState } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useCrm } from './CrmContext';
import { TaskEditor } from './TaskEditor';
import { TaskItem } from './TaskList';
import { Timeline } from './Timeline';
import { ExtensionPicker, Field, TagInput, Toggle } from './fields';
import { relativeTime } from '@/lib/relative-time';
import { buttonClass, errorMessage, formatTalk, formatWhen, inputClass } from './format';
import { LIFECYCLE_LABELS, type CrmCall, type CrmCompany, type CrmContact, type CrmTask, type Lifecycle } from './types';

export function CompanyEditor({ company, onClose, onSaved }: { company?: CrmCompany | null; onClose: () => void; onSaved?: (id: string) => void }) {
  const saveCompany = useMutation(backendApi.crm.saveCompany);
  const tags = useQuery<Array<{ tag: string }>>(backendApi.crm.listTags, {});
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: company?.name ?? '',
    website: company?.website ?? '',
    lifecycle: (company?.lifecycle ?? '') as Lifecycle | '',
    ownerExtension: company?.ownerExtension ?? '',
    ownerName: company?.ownerName ?? '',
    vip: company?.vip ?? false,
    tags: company?.tags ?? [],
    phonePatterns: (company?.phonePatterns ?? []).join(', '),
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const id = (await saveCompany({
        companyId: company?.id,
        name: form.name,
        website: form.website || undefined,
        lifecycle: form.lifecycle || undefined,
        ownerExtension: form.ownerExtension || undefined,
        ownerName: form.ownerName || undefined,
        vip: form.vip,
        tags: form.tags,
        phonePatterns: form.phonePatterns.split(',').map((pattern) => pattern.trim()).filter(Boolean),
      })) as string;
      toast.success(company ? 'Company updated' : 'Company added');
      onSaved?.(id);
      onClose();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={company ? 'Edit company' : 'New company'}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name *" htmlFor="company-name">
          <input id="company-name" autoFocus value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className={inputClass} />
        </Field>
        <Field label="Website" htmlFor="company-website">
          <input id="company-website" value={form.website} onChange={(event) => setForm({ ...form, website: event.target.value })} className={inputClass} placeholder="https://" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Stage" htmlFor="company-stage">
            <select id="company-stage" value={form.lifecycle} onChange={(event) => setForm({ ...form, lifecycle: event.target.value as Lifecycle | '' })} className={inputClass}>
              <option value="">Not set</option>
              {Object.entries(LIFECYCLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Account owner" htmlFor="company-owner">
            <ExtensionPicker id="company-owner" value={form.ownerExtension} onChange={(extension, name) => setForm({ ...form, ownerExtension: extension, ownerName: name ?? '' })} />
          </Field>
        </div>
        <Field label="Tags">
          <TagInput value={form.tags} onChange={(value) => setForm({ ...form, tags: value })} suggestions={(tags ?? []).map((row) => row.tag)} />
        </Field>
        <Field label="Number prefixes" htmlFor="company-patterns" hint="Optional, comma separated, e.g. 011 555 for a switchboard range.">
          <input id="company-patterns" value={form.phonePatterns} onChange={(event) => setForm({ ...form, phonePatterns: event.target.value })} className={inputClass} />
        </Field>
        <Toggle checked={form.vip} onChange={(vip) => setForm({ ...form, vip })} label="VIP account" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={buttonClass.secondary}>
            Cancel
          </button>
          <button type="submit" disabled={saving || !form.name.trim()} className={buttonClass.primary}>
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

type CompanyDetail = { company: CrmCompany; contacts: CrmContact[]; openTasks: CrmTask[]; contactsIncomplete: boolean; tasksIncomplete: boolean; lastConversation: CrmCall | null };

export function CompanyPanel({ companyId, onClose }: { companyId: string; onClose: () => void }) {
  const { data, openContact } = useCrm();
  const detail = useQueryState<CompanyDetail>(backendApi.crm.getCompany, { companyId });
  const deleteCompany = useMutation(backendApi.crm.deleteCompany);
  const [editing, setEditing] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  const company = detail.data?.company;
  const canManage = Boolean(data?.canManage);
  const nextTask = detail.data?.openTasks.slice().sort((a, b) => (a.dueAtMs ?? Infinity) - (b.dueAtMs ?? Infinity))[0];

  const remove = async () => {
    if (!company || !confirm(`Delete ${company.name}? Its contacts stay, without a company.`)) return;
    try {
      await deleteCompany({ companyId });
      toast.success('Company deleted');
      onClose();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Company" size="xl">
      {detail.error && <p className="text-red-600">{detail.error}</p>}
      {!company && !detail.error && <p className="text-gray-500">Loading…</p>}
      {company && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {company.vip && <Star className="inline w-5 h-5 mr-1 -mt-1 text-amber-500 fill-amber-400" />}
                {company.name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {[company.lifecycle ? LIFECYCLE_LABELS[company.lifecycle] : null, company.ownerName ? `Owner: ${company.ownerName}` : company.ownerExtension ? `Owner: ${company.ownerExtension}` : null]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {company.website && (
                <a href={/^https?:/i.test(company.website) ? company.website : `https://${company.website}`} target="_blank" rel="noreferrer" className="text-sm text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1">
                  {company.website} <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {company.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {company.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-3 text-sm space-y-2">
              <p><span className="text-gray-500">Last conversation: </span>{detail.data!.lastConversation ? `${formatWhen(detail.data!.lastConversation.startedAtMs)} · ${detail.data!.lastConversation.answeredByName ?? detail.data!.lastConversation.answeredBy ?? 'staff'} · ${formatTalk(detail.data!.lastConversation.talkSeconds)}` : 'None in the last 100 calls'}</p>
              <p><span className="text-gray-500">Next action: </span>{nextTask ? `${nextTask.title}${nextTask.dueAtMs ? ` · ${formatWhen(nextTask.dueAtMs)}` : ''}` : 'No open tasks'}</p>
              {(detail.data!.contactsIncomplete || detail.data!.tasksIncomplete) && <p className="text-amber-700 dark:text-amber-400">This account has more people or open tasks than shown here.</p>}
            </div>
            {canManage && (
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setEditing(true)} className={buttonClass.secondary}>
                  <Pencil className="w-4 h-4" /> Edit
                </button>
                <button type="button" onClick={() => setAddingTask(true)} className={buttonClass.secondary}>
                  <ListPlus className="w-4 h-4" /> Follow-up
                </button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-2">
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{company.stats.totalInteractions}</p>
                <p className="text-xs text-gray-500">Calls</p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-2">
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{company.stats.missedCalls}</p>
                <p className="text-xs text-gray-500">Missed</p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-2">
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatTalk(company.stats.totalDuration)}</p>
                <p className="text-xs text-gray-500">Talk time</p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-2">
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {company.stats.lastInteractionTimestamp ? relativeTime(new Date(company.stats.lastInteractionTimestamp * 1000)) : '—'}
                </p>
                <p className="text-xs text-gray-500">Last call</p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">People ({detail.data!.contacts.length})</h3>
              <div className="space-y-1">
                {detail.data!.contacts.map((contact) => (
                  <button
                    type="button"
                    key={contact.id}
                    onClick={() => openContact(contact.id)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">
                        {contact.vip && <Star className="inline w-3 h-3 mr-1 text-amber-500 fill-amber-400" />}
                        {contact.name}
                      </span>
                      <span className="block text-xs text-gray-500 truncate">{contact.jobTitle ?? contact.phones[0]?.number}</span>
                    </span>
                    <span className="text-xs text-gray-500 shrink-0">{contact.stats.totalInteractions} calls</span>
                  </button>
                ))}
              </div>
            </div>
            {canManage && (
              <button type="button" onClick={() => void remove()} className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:underline">
                <Trash2 className="w-4 h-4" /> Delete company
              </button>
            )}
          </div>
          <div className="lg:col-span-3 space-y-5">
            {detail.data!.openTasks.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Open tasks</h3>
                {detail.data!.openTasks.map((task) => (
                  <TaskItem key={task.id} task={task} />
                ))}
              </div>
            )}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">History (all contacts)</h3>
              <Timeline companyId={companyId} />
            </div>
          </div>
        </div>
      )}
      {editing && company && <CompanyEditor company={company} onClose={() => setEditing(false)} />}
      {addingTask && company && <TaskEditor companyId={company.id} subjectName={company.name} onClose={() => setAddingTask(false)} />}
    </Modal>
  );
}
